import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, User, Calendar, MessageSquare } from 'lucide-react';
import { FormComponentType } from '../df-form-controls/df-form-preview-interfaces';
import { toastService } from '../services/toast.service';
import './RaiseIssueModal.scss';

// Issue status enum
enum EIssueStatus {
  OPEN = 'Open',
  IN_PROGRESS = 'In-Progress',
  REJECTED = 'Rejected',
  RESOLVE = 'Resolve'
}

// Issue interface
interface IIssue {
  _id?: string;
  issueNumber?: string;
  title?: string;
  description?: string;
  workOrderNumber?: string;
  assetNumber?: string;
  formTemplateId?: string;
  component?: FormComponentType;
  priority?: 'High' | 'Medium' | 'Low';
  status?: string;
  assignee?: string;
  comments?: string;
  createdAt?: string;
}

interface IRaiseIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdIssue?: IIssue) => void;
  component?: FormComponentType;
  formTemplateId?: string;
  notes?: string;
  attachments?: File[] | null;
  issue?: IIssue | null;
  // Optional API callbacks - if not provided, modal will just call onSuccess
  onCreateIssue?: (issueData: any, attachments: File[]) => Promise<IIssue>;
  onUpdateIssue?: (issueId: string, updateData: any) => Promise<void>;
  // Optional user info
  user?: {
    firstName?: string;
    lastName?: string;
  };
  // Optional available users list
  availableUsers?: string[];
}

export const RaiseIssueModal: React.FC<IRaiseIssueModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  component,
  formTemplateId,
  notes = '',
  attachments = null,
  issue = null,
  onCreateIssue,
  onUpdateIssue,
  user,
  availableUsers = [
    'Priya Das',
    'Maria Garcia',
    'John Smith',
    'Sarah Johnson'
  ]
}) => {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [workOrderNumber, setWorkOrderNumber] = useState<string>('');
  const [assetNumber, setAssetNumber] = useState<string>('');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [status, setStatus] = useState<string>(EIssueStatus.OPEN);
  const [assignee, setAssignee] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isEditMode = !!issue;

  useEffect(() => {
    if (isOpen) {
      if (issue) {
        // Edit mode: populate with issue data
        setTitle(issue.title || '');
        setDescription(issue.description || '');
        setWorkOrderNumber(issue.workOrderNumber || '');
        setAssetNumber(issue.assetNumber || '');
        setPriority(issue.priority || 'Medium');
        // Use enum value or default to OPEN
        const issueStatus = issue.status || EIssueStatus.OPEN;
        // Normalize to enum values if needed (handle legacy values)
        const normalizedStatus = 
          issueStatus === EIssueStatus.OPEN || issueStatus === EIssueStatus.IN_PROGRESS || issueStatus === EIssueStatus.REJECTED || issueStatus === EIssueStatus.RESOLVE
            ? issueStatus
            : EIssueStatus.OPEN;
        setStatus(normalizedStatus);
        setAssignee(issue.assignee || '');
        setComments(issue.comments || '');
      } else if (component) {
        // Create mode: initialize form with component data
        const componentLabel = component.basic?.label || 'Component';
        setTitle(`${componentLabel} - Issue`);
        
        // Build description from component value and notes
        let desc = `Issue raised for component: ${componentLabel}`;
        if (component.basic && 'value' in component.basic && component.basic.value) {
          desc += `\nComponent Value: ${component.basic.value}`;
        }
        if (notes) {
          desc += `\nNotes: ${notes}`;
        }
        setDescription(desc);
        setComments(notes || '');
      }
    }
  }, [isOpen, component, notes, issue]);

  const updateIssueStatus = async (newStatus: string) => {
    if (!issue) return;

    setIsSubmitting(true);

    try {
      const updateData = {
        title: String(title || '').trim(),
        description: String(description || '').trim(),
        workOrderNumber: String(workOrderNumber || '').trim() || '',
        assetNumber: String(assetNumber || '').trim() || '',
        formTemplateId: issue.formTemplateId || '',
        component: issue.component || {},
        priority,
        assignee: String(assignee || '').trim() || undefined,
        status: newStatus,
        comments: String(comments || '').trim() || ''
      };

      if (onUpdateIssue && issue._id) {
        await onUpdateIssue(issue._id, updateData);
      }
      setStatus(newStatus);
      
      const statusMessages: Record<string, string> = {
        [EIssueStatus.IN_PROGRESS]: 'Issue accepted and moved to In-Progress',
        [EIssueStatus.RESOLVE]: 'Issue resolved successfully',
        [EIssueStatus.REJECTED]: 'Issue rejected'
      };
      
      toastService.showSuccess(statusMessages[newStatus] || 'Issue status updated successfully');
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      const errorMessage = error?.message || error?.error?.message || 'Failed to update issue status. Please try again.';
      toastService.showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccept = async () => {
    await updateIssueStatus(EIssueStatus.IN_PROGRESS);
  };

  const handleResolve = async () => {
    await updateIssueStatus(EIssueStatus.RESOLVE);
  };

  const handleReject = async () => {
    await updateIssueStatus(EIssueStatus.REJECTED);
  };

  const handleSubmit = async () => {
    if (!String(title || '').trim()) {
      toastService.showError('Title is required');
      return;
    }

    if (!String(description || '').trim()) {
      toastService.showError('Description is required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && issue) {
        // Update existing issue
        const updateData = {
          title: String(title || '').trim(),
          description: String(description || '').trim(),
          workOrderNumber: String(workOrderNumber || '').trim() || '',
          assetNumber: String(assetNumber || '').trim() || undefined,
          formTemplateId: issue.formTemplateId || '',
          component: issue.component || {},
          priority,
          assignee: String(assignee || '').trim() || undefined,
          status: status,
          comments: String(comments || '').trim() || ''
        };

        if (onUpdateIssue && issue._id) {
          await onUpdateIssue(issue._id, updateData);
        }
        toastService.showSuccess('Issue updated successfully');
        
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        // Create new issue
        if (!component) {
          toastService.showError('Component is required');
          setIsSubmitting(false);
          return;
        }
        
        if (!formTemplateId || String(formTemplateId || '').trim() === '') {
          toastService.showError('Form template is required');
          setIsSubmitting(false);
          return;
        }

        // Prepare component data for API - include all component properties
        const componentData = {
          ...component,
          basic: {
            ...component.basic,
            comments: String(notes || '')
          }
        };

        const issueData = {
          title: String(title || '').trim(),
          workOrderNumber: String(workOrderNumber || '').trim() || undefined,
          assetNumber: String(assetNumber || '').trim() || undefined,
          component: componentData,
          description: String(description || '').trim(),
          formTemplateId,
          assignee: String(assignee || '').trim() || undefined,
          priority,
          status: status,
          comments: String(comments || '').trim() || ''
        };

        let createdIssue: IIssue | undefined;
        if (onCreateIssue) {
          createdIssue = await onCreateIssue(issueData, attachments || []);
        }
        toastService.showSuccess('Issue raised successfully');
        
        if (onSuccess) {
          onSuccess(createdIssue);
        }
        onClose();
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.error?.message || (isEditMode ? 'Failed to update issue. Please try again.' : 'Failed to raise issue. Please try again.');
      toastService.showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="raise-issue-modal-overlay" onClick={onClose}>
      <div className="raise-issue-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="raise-issue-modal-header">
          <div className="raise-issue-modal-header-left">
            <AlertTriangle className="raise-issue-modal-icon" size={20} />
            <div className="raise-issue-modal-header-text">
              <div className="raise-issue-modal-title-main">Issue Details</div>
            </div>
          </div>
          <button
            className="raise-issue-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="raise-issue-modal-content">
          <div className="raise-issue-fields-grid">
            {/* Issue Number - Show only in edit mode */}
            {isEditMode && issue?.issueNumber && (
              <div className="raise-issue-field">
                <label className="raise-issue-field-label">Issue ID</label>
                <input
                  type="text"
                  className="raise-issue-field-input raise-issue-field-readonly"
                  value={issue.issueNumber}
                  readOnly
                />
              </div>
            )}

            {/* Title */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Title <span className="raise-issue-required">*</span></label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter issue title"
                readOnly={isEditMode}
              />
            </div>

            {/* Work Order */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Work Order <span className="raise-issue-required">*</span></label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={workOrderNumber}
                onChange={(e) => setWorkOrderNumber(e.target.value)}
                placeholder="N/A"
                readOnly={isEditMode}
              />
            </div>

            {/* Asset Number */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Asset Number <span className="raise-issue-required">*</span></label>
              <input
                type="text"
                className={`raise-issue-field-input ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={assetNumber}
                onChange={(e) => setAssetNumber(e.target.value)}
                placeholder="Enter asset number"
                readOnly={isEditMode}
              />
            </div>

            {/* Raised By */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Raised By</label>
              <div className="raise-issue-field-value-with-icon">
                <User size={16} />
                <span>{user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User' : 'User'}</span>
              </div>
            </div>

            {/* Created On */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Created On</label>
              <div className="raise-issue-field-value-with-icon">
                <Calendar size={16} />
                <span>
                  {isEditMode && issue?.createdAt
                    ? new Date(issue.createdAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    : new Date().toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                </span>
              </div>
            </div>

            {/* Description - Full Width */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label">Description <span className="raise-issue-required">*</span></label>
              <textarea
                className={`raise-issue-field-textarea ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter issue description"
                rows={4}
                readOnly={isEditMode}
              />
            </div>

            {/* Status */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Status <span className="raise-issue-required">*</span></label>
              <select
                className={`raise-issue-field-select ${!isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={!isEditMode}
              >
                <option value={EIssueStatus.OPEN}>{EIssueStatus.OPEN}</option>
                <option value={EIssueStatus.IN_PROGRESS}>{EIssueStatus.IN_PROGRESS}</option>
                <option value={EIssueStatus.REJECTED}>{EIssueStatus.REJECTED}</option>
                <option value={EIssueStatus.RESOLVE}>{EIssueStatus.RESOLVE}</option>
              </select>
            </div>

            {/* Assignee */}
            <div className="raise-issue-field">
              <label className="raise-issue-field-label">Assignee <span className="raise-issue-required">*</span></label>
              <select
                className="raise-issue-field-select"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
              >
                <option value="">Unassigned</option>
                {availableUsers.map(userName => (
                  <option key={userName} value={userName}>
                    {userName}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label">Priority <span className="raise-issue-required">*</span></label>
              <div className="raise-issue-priority-buttons">
                <button
                  type="button"
                  className={`priority-button priority-low ${priority === 'Low' ? 'active' : ''}`}
                  onClick={() => setPriority('Low')}
                >
                  Low
                </button>
                <button
                  type="button"
                  className={`priority-button priority-medium ${priority === 'Medium' ? 'active' : ''}`}
                  onClick={() => setPriority('Medium')}
                >
                  Medium
                </button>
                <button
                  type="button"
                  className={`priority-button priority-high ${priority === 'High' ? 'active' : ''}`}
                  onClick={() => setPriority('High')}
                >
                  High
                </button>
              </div>
            </div>

            {/* Comments - Full Width */}
            <div className="raise-issue-field raise-issue-field-full-width">
              <label className="raise-issue-field-label-with-icon">
                <MessageSquare size={16} />
                <span>Comments</span>
              </label>
              {!comments && (
                <div className="raise-issue-no-comments">No comments yet</div>
              )}
              <textarea
                className={`raise-issue-field-textarea raise-issue-comments-textarea ${isEditMode ? 'raise-issue-field-readonly' : ''}`}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add a comment..."
                rows={4}
                readOnly={isEditMode}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="raise-issue-modal-actions">
          <div className="raise-issue-modal-actions-buttons">
            {/* Action buttons - only show in edit mode and when status allows actions */}
            {isEditMode && issue && (
              <>
                {status === EIssueStatus.OPEN && (
                  <button
                    className="raise-issue-modal-button raise-issue-modal-button-accept"
                    onClick={handleAccept}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Accept'}
                  </button>
                )}
                {status === EIssueStatus.IN_PROGRESS && (
                  <button
                    className="raise-issue-modal-button raise-issue-modal-button-resolve"
                    onClick={handleResolve}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Resolve'}
                  </button>
                )}
                {status === EIssueStatus.OPEN && (
                  <button
                    className="raise-issue-modal-button raise-issue-modal-button-reject"
                    onClick={handleReject}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Reject'}
                  </button>
                )}
              </>
            )}
            {!isEditMode && (
              <button
                className="raise-issue-modal-button raise-issue-modal-button-save"
                onClick={handleSubmit}
                disabled={isSubmitting || !String(title || '').trim() || !String(description || '').trim()}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to document body for full-page overlay
  return createPortal(modalContent, document.body);
};
